import { FC, useEffect, useState } from "react";
import PageTabNavigator from "./NavBar/PageTabNavigator";
import { Element } from "react-scroll";
import Banner from "./Banner/Banner";
import AIAssistant from "./AI Assistant/AI Assistant";
import NavigationMenu from "./NavBar/NavigationMenu";
import ChatBotPanel from "./ChatBot/ChatBotPanel";
import { LearnByTopic } from "./LearnByTopic/LearnByTopic";
import { LearnByRole } from "./LearnByRole/LearnByRole";
import Skills from "./Skills/Skills";
import SuccessStories from "./SuccessStories/SuccessStories";
import Featured from "./Featured/Featured";
import BannerMobile from "./Banner/BannerMobile";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { fetchTranslations } from '../i18n';
import { withTranslation } from 'react-i18next';
import { mergeStyleSets } from "@fluentui/react";
import { getLanguageCodeFromUrl } from "../utilities/LocalizationHelper";
import axiosClient from "../utilities/HttpClient";
import appInsights, { generateCookieId } from "../utilities/AppInsights";
import { useSectionorder } from "../hooks/useHooks";
import SpotLight from "./SpotLight/SpotLight";
import { scroller } from 'react-scroll';
import { scrollToElement } from "../utilities/scrollToElement";


interface ComponentOrder {
    Componentname: string;
    label: string;
    id: string;
    order: number;
    insideTab: boolean;
    Enable: boolean;
}

interface LandingPageProps {
    showPanel: boolean;
    togglePanel: () => void;
    closePanel: () => void;
    botAction: string;
    initialText: string;
    onButtonClick: (action: string, text: string) => void;
    setBotAction: (action: string) => void;
    setinitialText: (text: string) => void;

}
interface SupportedLanguage {
    Locale: string;
    EnglishName: string;
    LocalizedName: string;
    ShowChatBot: boolean;
    Direction: string
}
let supportedLanguages: SupportedLanguage[] = [];
const LandingPage: FC<LandingPageProps> = ({
    showPanel,
    togglePanel,
    closePanel, botAction,
    onButtonClick,
    initialText,
    setBotAction,
    setinitialText
}) => {
    const [orderedComponents, setOrderedComponents] = useState<ComponentOrder[]>([]);
    const styles = mergeStyleSets({
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000, // Ensure it is above other content
        }
    })
    const { data: SectionorderData, isLoading: isLoadingSectionorder, error } = useSectionorder();
    const [isLoading, setIsLoading] = useState(false);
    const [LanguageDirection, setLanguageDirection] = useState("Left");
    const [isLoadingLocalization, setIsLoadingLocalization] = useState(true);
    const { t, i18n } = useTranslation();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isUiReady, setIsUiReady] = useState(false);

    const { lang } = useParams<{ lang?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const componentMap: { [key: string]: JSX.Element } = {
        LearnByTopic: <LearnByTopic />,
        LearnByRole: <LearnByRole />,
        Skills: <Skills />,
        SuccessStories: <SuccessStories />,
        SpotLight: <SpotLight />,
        Banner: isMobile
            ? <BannerMobile onButtonClick={onButtonClick} isLoading={false} />
            : <Banner onButtonClick={onButtonClick} isLoading={false} />,
        Featured: <Featured />,
        AIAssistant: <AIAssistant onButtonClick={onButtonClick} isLoading={isLoading} />,
    };
    useEffect(() => {
        localStorage.clear();
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!isLoadingSectionorder && SectionorderData?.SectionSortOrder) {
            // Filter out components with Enable: false and sort by order
            const sortedComponents = SectionorderData.SectionSortOrder
                .filter(component => component.Enable)
                .sort((a, b) => a.order - b.order);
            setOrderedComponents(sortedComponents);
        }
    }, [SectionorderData, isLoadingSectionorder]);


    useEffect(() => {
        const changeLanguage = async (newLang: string) => {
            setIsLoadingLocalization(true);
            await fetchTranslations(newLang);
            await i18n.changeLanguage(newLang);
            await fetchSupportedLanguages();
            setIsLoadingLocalization(false);
        };
        const detectedLang = i18n.language || 'en-US'; // Get detected language or fall back to 'en-us'
        changeLanguage(lang || detectedLang);

        appInsights.trackPageView({
            name: window.location.pathname,
            properties: { visitorId: generateCookieId() }
        })

    }, [lang, i18n, location, navigate]);

    const fetchSupportedLanguages = async (): Promise<void> => {
        try {
            const languageCode = getLanguageCodeFromUrl(window.location.href);
            let supportedLanguage;
            if (!localStorage.getItem('supportedLanguages')) {
                const response = await axiosClient.get<{ SupportedLanguages: SupportedLanguage[] }>('/BlobConfig/SupportedLanguage');
                const languages = response.data.SupportedLanguages;
                supportedLanguage = languages?.find(item => item.Locale.toLowerCase() === languageCode.toLowerCase());
                if (supportedLanguage) {
                    setLanguageDirection(supportedLanguage.Direction);
                }
                localStorage.setItem('supportedLanguages', JSON.stringify(supportedLanguages));
            } else {
                const cachedLanguages = JSON.parse(localStorage.getItem('supportedLanguages') || '[]');
                supportedLanguage = cachedLanguages?.find(item => item.Locale.toLowerCase() === languageCode.toLowerCase());
                setLanguageDirection(supportedLanguage.Direction);

            }
            document.documentElement.setAttribute("dir", supportedLanguage.Direction);
            document.body.classList.add(`langdirection-${supportedLanguage.Direction}`);
            document.documentElement.setAttribute("lang", i18n.language);
        } catch (error) {
            console.error('Failed to fetch supported languages:', error);
            if (window.location.pathname !== '/error') {
                window.location.href = '/error';
            }
        }
    };
    //const handleButtonClick = (action: string) => {
    //    setBotAction(action);
    //    togglePanel();
    //};
    const navigateToSection = (id: string) => {
        const hash = `#${id}`;
        if (window.location.hash === hash) {
            scrollToElement(id); // Same hash, scroll manually
        } else {
            window.location.hash = hash; // Will trigger scroll via hashchange listener
        }
    };

    useEffect(() => {
        if (!orderedComponents.length) return;

        const scrollToHash = () => {
            const hash = window.location.hash;
            if (!hash) return;
            const id = hash.substring(1);
            scrollToElement(id);
        };

        scrollToHash(); // Initial
        window.addEventListener("hashchange", scrollToHash);
        return () => window.removeEventListener("hashchange", scrollToHash);
    }, [orderedComponents]);


    if (isLoadingSectionorder || isLoadingLocalization) {
        return (
            <div><Spinner className={styles.overlay} size={SpinnerSize.large}></Spinner></div>

        );
    }
    return (
        <>
            <ChatBotPanel
                isOpen={showPanel}
                togglePanel={togglePanel}
                botAction={botAction}
                initialText={initialText}
                setBotAction={setBotAction}
                setinitialText={setinitialText}
                isLoadingData={isLoading}
                setIsLoading={setIsLoading}
            />

            {/* Components outside tab */}
            {orderedComponents.filter(c => !c.insideTab && c.Enable).map((component, index) => (
                <div key={index}>{componentMap[component.Componentname]}</div>
            ))}
            <PageTabNavigator
                sections={orderedComponents
                    .filter(c => c.insideTab)
                    .map(c => ({ label: c.label, id: c.id, Componentname: c.Componentname }))}
            />
            <NavigationMenu
                sections={orderedComponents
                    .filter(c => c.insideTab)
                    .map(c => ({ label: c.label, id: c.id, Componentname: c.Componentname }))} />
            {/* Components inside tab */}
            <div className="tab-container">
                {orderedComponents.filter(c => c.insideTab && c.Enable).map((component, index) => (
                    // <Element key={index} id={component.id} name={component.id} data-label={component.label} className="section">
                    //     {componentMap[component.Componentname]}
                    // </Element>
                    <Element key={index} name={component.id} data-label={component.label} className="section">
                        <div id={component.id} className="section">
                            {componentMap[component.Componentname]}
                        </div>
                    </Element>
                ))}
            </div>
        </>
    );

};

export default withTranslation()(LandingPage);
